import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { OrganisationTagExternal, Story } from '../../../services';
import { layoutPath, StoryFollowChip, TagFollowChip } from '../../../shared';

import classes from './PersonalizedNewsFeed.module.scss';

const ONBOARDING_STORIES_CAP = 15;
const ONBOARDING_TAGS_CAP = 15;

interface MoreCountArgs {
  totalCount: number;
  visibleCount: number;
}

interface PersonalizedNewsFeedProps {
  followableStories: Story[];
  followableTags: OrganisationTagExternal[];
  titleId: string;
}

export const PersonalizedNewsFeed: FunctionComponent<PersonalizedNewsFeedProps> = memo(
  ({ followableStories, followableTags, titleId }) => {
    const { t: ctxT } = useTranslation('', { keyPrefix: 'newsFeedOnboarding' });

    const renderMoreCount = useCallback(
      ({ totalCount, visibleCount }: MoreCountArgs) => {
        if (totalCount <= visibleCount) {
          return null;
        }

        return (
          <span className={classes['personalized-news-feed__more-count']}>
            {ctxT('more-count', { count: totalCount - visibleCount })}
          </span>
        );
      },
      [ctxT]
    );

    const storiesGroup = useMemo(() => {
      if (!followableStories.length) {
        return null;
      }

      return (
        <div className={classes['personalized-news-feed__group']}>
          <div>
            <h3 className={classes['personalized-news-feed__group-title']}>{ctxT('stories')}</h3>
            <div className={classes['personalized-news-feed__group-description']}>
              {ctxT('stories-description')}
            </div>
          </div>
          <div className={classes['personalized-news-feed__chips']}>
            {followableStories.slice(0, ONBOARDING_STORIES_CAP).map((story) => (
              <StoryFollowChip key={story.id} story={story} truncate />
            ))}
            {renderMoreCount({
              totalCount: followableStories.length,
              visibleCount: ONBOARDING_STORIES_CAP,
            })}
          </div>
        </div>
      );
    }, [ctxT, followableStories, renderMoreCount]);

    const tagsGroup = useMemo(() => {
      if (!followableTags.length) {
        return null;
      }

      return (
        <div className={classes['personalized-news-feed__group']}>
          <div>
            <h3 className={classes['personalized-news-feed__group-title']}>{ctxT('tags')}</h3>
            <div className={classes['personalized-news-feed__group-description']}>
              {ctxT('tags-description')}
            </div>
          </div>
          <div className={classes['personalized-news-feed__chips']}>
            {followableTags.slice(0, ONBOARDING_TAGS_CAP).map((tag) => (
              <TagFollowChip key={tag.id} tag={tag} truncate />
            ))}
            {renderMoreCount({
              totalCount: followableTags.length,
              visibleCount: ONBOARDING_TAGS_CAP,
            })}
          </div>
        </div>
      );
    }, [ctxT, followableTags, renderMoreCount]);

    return (
      <div className={classes['personalized-news-feed']}>
        <div>
          <h2 id={titleId} className={classes['personalized-news-feed__title']}>
            {ctxT('title')}
          </h2>
          <div className={classes['personalized-news-feed__sub-title']}>{ctxT('description')}</div>
        </div>

        {storiesGroup}

        {tagsGroup}

        <Link
          to={layoutPath('/myTopics')}
          className={classes['personalized-news-feed__settings-link']}
        >
          {ctxT('all-options')}
        </Link>
      </div>
    );
  }
);
